<script setup>
import { computed, useSlots } from 'vue';
import {
    TooltipContent,
    TooltipPortal,
    TooltipRoot,
    TooltipTrigger,
} from 'reka-ui';
import { tooltipVariants } from './index.ts';

defineProps({
    /**
     * Plain-text tooltip body. Used as a shorthand when richer content
     * isn't required. Ignored when the `#content` slot is provided.
     */
    text: {
        type: String,
        default: '',
    },
    /**
     * Which side of the trigger the tooltip opens on.
     * @values 'top', 'right', 'bottom', 'left'
     * @default 'top'
     */
    side: {
        type: String,
        default: 'top',
        validator: (v) => ['top', 'right', 'bottom', 'left'].includes(v),
    },
    /**
     * Alignment of the tooltip against the trigger along the chosen `side`.
     * @values 'start', 'center', 'end'
     * @default 'center'
     */
    align: {
        type: String,
        default: 'center',
        validator: (v) => ['start', 'center', 'end'].includes(v),
    },
    /**
     * Distance in pixels between the trigger and the tooltip.
     * @default 6
     */
    sideOffset: {
        type: Number,
        default: 6,
    },
    /**
     * Per-instance delay override. When omitted, the surrounding
     * `TooltipProvider`'s `delayDuration` is used.
     */
    delayDuration: {
        type: Number,
        default: undefined,
    },
    /**
     * Per-instance hoverable-content override. When omitted, the surrounding
     * `TooltipProvider`'s `disableHoverableContent` is used.
     */
    disableHoverableContent: {
        type: Boolean,
        default: undefined,
    },
    /**
     * When `true`, the trigger never opens the tooltip — useful when an
     * action button is conditionally non-interactive but the tooltip would
     * otherwise still appear.
     * @default false
     */
    disabled: {
        type: Boolean,
        default: false,
    },
    /**
     * Extra classes merged onto the tooltip content panel.
     */
    contentClass: {
        type: [String, Array, Object],
        default: undefined,
    },
});

const slots = useSlots();

const isOpen = defineModel('open', { type: Boolean, default: undefined });

const ui = computed(() => tooltipVariants());

const hasContentSlot = computed(() => Boolean(slots.content));
</script>

<template>
    <TooltipRoot
        v-model:open="isOpen"
        :delay-duration="delayDuration"
        :disable-hoverable-content="disableHoverableContent"
        :disabled="disabled"
    >
        <TooltipTrigger as-child>
            <slot />
        </TooltipTrigger>

        <TooltipPortal>
            <TooltipContent
                :side="side"
                :align="align"
                :side-offset="sideOffset"
                :class="ui.content({ class: contentClass })"
            >
                <slot
                    v-if="hasContentSlot"
                    name="content"
                />
                <template v-else>
                    {{ text }}
                </template>
            </TooltipContent>
        </TooltipPortal>
    </TooltipRoot>
</template>
